import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeftRight, CalendarClock, FileSignature, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { useMedia } from "@/hooks/use-media";
import { Button } from "@/components/ui/button";
import { Initials } from "@/components/ui/list-row";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import FattoFuori from "@/components/crm/FattoFuori";
import PersoDialog, { fraTreMesi } from "@/components/crm/PersoDialog";
import { eur } from "@/lib/formato";
import { arrivo, erroreDi, giorno, giornoDi, giornoLungo, giornoOra, oggi, ora, passato, statoCommerciale, valoreBreve } from "@/lib/crm";
import { cn } from "@/lib/utils";

/* Le colonne sono gli stati commerciali, nell'ordine in cui un cliente li
   attraversa. No show e Perso stanno in fondo, staccati: sono uscite dal percorso,
   non tappe.

   Si trascina, e lo stato lo decidono ancora i fatti (D5, D8): ogni spostamento
   passa da sposta_cliente nel database, che scrive il fatto (l'esito della visita,
   il perso col motivo, la trattativa) o risponde col gesto da aprire (fissare la
   visita, inviare il preventivo, firmare il contratto). Qui non si decide niente:
   si chiede, si mostra la risposta, si offre l'annullamento. */
const PERCORSO = ["da_confermare", "appuntamento_fatto", "preventivo_inviato", "in_trattativa", "chiuso"];
const USCITE = ["no_show", "perso"];
const COLONNE = [...PERCORSO, ...USCITE];
const ANNULLA_PER_MS = 8000; // il database ne concede 30: il resto è margine per la rete
const TIENI_PREMUTO_MS = 350;

/** Cosa c'è da fare su questo cliente, in una riga: l'esito mancante, l'appuntamento, la prossima azione. */
export function prossimo(r) {
  if (r.appuntamento_senza_esito_at) {
    const scaduto = passato(r.appuntamento_senza_esito_at);
    return {
      testo: scaduto
        ? `Esito mancante · ${giorno(r.appuntamento_senza_esito_at)}`
        : `Showroom · ${giornoOra(r.appuntamento_senza_esito_at)}`,
      urgente: scaduto,
    };
  }
  if (r.prossima_azione_at) {
    return {
      testo: `${r.prossima_azione || "Prossima azione"} · ${giorno(r.prossima_azione_at)}`,
      urgente: passato(r.prossima_azione_at),
    };
  }
  // In «Nuovo appuntamento» senza visita: al posto della data, «da fissare» (0041).
  if (r.stato === "da_confermare") return { testo: "Showroom · da fissare", urgente: false };
  return null;
}

/** Prima ciò che è in ritardo, poi ciò che ha una data (la più vicina prima), poi il resto nell'ordine in
 *  cui è arrivato. */
function ordinaPerUrgenza(righe) {
  const quando = (r) => r.appuntamento_senza_esito_at || r.prossima_azione_at || null;
  return [...righe].sort((a, b) => {
    const pa = prossimo(a);
    const pb = prossimo(b);
    if (Boolean(pa?.urgente) !== Boolean(pb?.urgente)) return pa?.urgente ? -1 : 1;
    const qa = quando(a);
    const qb = quando(b);
    if (qa && qb) return qa.localeCompare(qb);
    if (qa || qb) return qa ? -1 : 1;
    return 0;
  });
}

/** In cima i lead che nessuno ha ancora aperto, dal più recente; poi l'urgenza. La usa anche l'elenco
 *  dei clienti («Prima i più urgenti»): un lead appena arrivato dalla landing non finisce in fondo. */
export function ordina(righe, codice) {
  const nuovi = righe.filter((r) => r.da_vedere).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  const resto = righe.filter((r) => !r.da_vedere);
  if (codice === "chiuso") {
    return [...nuovi, ...resto.sort((a, b) => (b.firmato_il || "").localeCompare(a.firmato_il || ""))];
  }
  return [...nuovi, ...ordinaPerUrgenza(resto)];
}

const etichetta = (codice) => statoCommerciale(codice).label;
const dettaglio = (e) => e?.response?.data?.detail;

/** La visita passata senza esito, se la carta è ancora in «Nuovo appuntamento»: si segna dalla carta. */
const esitoDaSegnare = (r) => r.stato === "da_confermare" && r.appuntamento_senza_esito_at && passato(r.appuntamento_senza_esito_at);

const TONI = {
  ritardo: "bg-warning-bg text-warning-ink",
  firmato: "bg-success-bg text-success-ink",
  oggi: "bg-brand-subtle text-brand-ink",
  normale: "bg-surface-2 text-ink-2",
};

function Chip({ children }) {
  return (
    <span className="inline-flex h-5 max-w-[9rem] items-center truncate rounded-xs bg-surface-3 px-1.5 text-micro text-ink-2">
      {children}
    </span>
  );
}

/** Quello che sta dentro una carta: lo stesso nel Kanban e nel fantasma che segue il dito. */
function Contenuto({ r, conEsito = false }) {
  const p = prossimo(r) || (r.firmato_il ? { testo: `Firmato ${giorno(r.firmato_il)}`, firmato: true } : null);
  const quando = r.appuntamento_senza_esito_at || r.prossima_azione_at;
  const tono = p?.urgente ? "ritardo" : p?.firmato ? "firmato" : quando && giornoDi(quando) === oggi() ? "oggi" : "normale";
  const Icona = p?.urgente ? TriangleAlert : p?.firmato ? FileSignature : CalendarClock;
  return (
    <>
      <span className="flex items-start justify-between gap-inside">
        <span className="line-clamp-2 min-w-0 font-bold text-ink-strong">{r.nome}</span>
        {r.da_vedere ? (
          <span className="shrink-0 rounded-xs bg-brand px-1.5 py-0.5 text-micro text-on-brand" data-testid={`kanban-nuovo-${r.id}`}>
            Nuovo
          </span>
        ) : r.venditore ? (
          <span title={r.venditore}>
            <Initials name={r.venditore} size={24} />
            <span className="sr-only">, lo segue {r.venditore}</span>
          </span>
        ) : null}
      </span>
      <span className="text-caption font-normal tabular-nums text-ink-3" data-testid={`kanban-arrivo-${r.id}`}>
        arrivato {arrivo(r.created_at)}
      </span>
      {r.storico_cliente ? (
        <span className="text-caption font-normal text-ink-3" data-testid={`kanban-storico-${r.id}`}>
          Cliente da prima del gestionale
        </span>
      ) : null}
      <span className="flex flex-wrap items-center gap-1.5">
        {r.valore !== null && r.valore !== undefined ? (
          <span className="font-bold tabular-nums text-ink-strong">{valoreBreve(r)}</span>
        ) : null}
        {r.sede ? <Chip>{r.sede}</Chip> : null}
        <Chip>{r.fonte || "fonte non indicata"}</Chip>
      </span>
      {p && !conEsito ? (
        <span className={cn("flex items-start gap-1.5 rounded-xs px-2 py-1 text-caption", TONI[tono])}>
          <Icona className="mt-px size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">{p.testo}</span>
        </span>
      ) : null}
    </>
  );
}

// La cornice della carta. grid-cols-1 = minmax(0, 1fr): una riga che non va a capo non allarga la carta.
const CARTA = "grid grid-cols-1 gap-2 rounded-lg border border-line bg-surface p-3 text-left";

function somma(righe) {
  const conValore = righe.filter((r) => r.valore !== null && r.valore !== undefined);
  if (!conValore.length) return null;
  const firmati = conValore.every((r) => r.valore_nota === "contratto" || r.valore_nota === "venduto");
  const venduti = conValore.some((r) => r.valore_nota === "venduto"); // dai fogli Excel di prima del gestionale (0049)
  const stimati = conValore.some((r) => r.valore_nota === "stimato");
  const totale = eur(
    conValore.reduce((t, r) => t + Number(r.valore), 0),
    { decimali: 0 },
  );
  return { testo: `${stimati ? "~ " : ""}${totale}`, nota: firmati ? (venduti ? "venduti" : "firmati") : "stimati" };
}

/* Il riepilogo sopra il tabellone: le sette colonne con quanti e quanto, a un tocco dalla colonna.
   Il percorso ha il bordo blu, le uscite (No show, Perso) restano grigie. */
function Riepilogo({ perStato, onVai }) {
  return (
    <nav aria-label="Colonne" className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0" data-testid="kanban-riepilogo">
      <div className="flex w-max gap-1.5 lg:grid lg:w-full lg:grid-cols-7">
        {COLONNE.map((c) => {
          const righe = perStato[c] || [];
          const t = somma(righe);
          const percorso = PERCORSO.indexOf(c) > -1 && c !== "chiuso";
          return (
            <button
              key={c}
              type="button"
              onClick={() => onVai(c)}
              className={cn(
                "flex min-h-11 min-w-0 flex-col gap-0.5 rounded-md border px-3 py-2 text-left transition-colors duration-150 ease-enter hover:bg-surface-2",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
                percorso ? "border-brand bg-surface" : "border-line bg-surface-2",
              )}
            >
              <span className={cn("truncate text-caption", percorso ? "text-brand-ink" : "text-ink-2")}>{statoCommerciale(c).label}</span>
              <span className="flex items-baseline justify-between gap-1.5">
                <span className={cn("font-figure text-h3", c === "no_show" && righe.length ? "text-danger" : "text-ink-strong")}>
                  {righe.length}
                </span>
                <span className="hidden truncate text-caption font-normal tabular-nums text-ink-2 lg:inline">{t ? t.testo : ""}</span>
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function Colonna({ codice, righe, evidenziata, rilascia, carta }) {
  const s = statoCommerciale(codice);
  const t = somma(righe);
  const urgenti = righe.filter((r) => prossimo(r)?.urgente).length;
  return (
    <section
      aria-labelledby={`kanban-${codice}-titolo`}
      data-testid={`kanban-colonna-${codice}`}
      data-colonna={codice}
      data-sopra={evidenziata || undefined}
      className={cn(
        "flex w-[82vw] max-w-[300px] shrink-0 snap-start flex-col gap-2 rounded-xl border-[1.5px] p-2.5 transition-colors duration-150 ease-enter md:w-[260px]",
        evidenziata ? "border-brand bg-brand-subtle" : "border-transparent bg-track",
      )}
    >
      <header className="grid gap-0.5 px-1 pb-1 pt-0.5">
        <div className="flex items-center gap-inside">
          <h2 id={`kanban-${codice}-titolo`} className="min-w-0 flex-1 truncate font-bold text-ink-strong">
            {s.label}
          </h2>
          <span
            className="inline-flex h-[22px] items-center rounded-xs bg-surface px-1.5 text-caption tabular-nums text-ink-2"
            data-testid={`kanban-conta-${codice}`}
          >
            {righe.length}
          </span>
        </div>
        <p className="flex items-center justify-between gap-inside text-caption font-normal text-ink-2">
          <span className="tabular-nums">{t ? `${t.testo} ${t.nota}` : " "}</span>
          {urgenti ? <span className="font-semibold text-warning-ink">{urgenti} in ritardo</span> : null}
        </p>
      </header>
      {rilascia ? (
        <div
          className="flex h-24 items-center justify-center rounded-lg border-2 border-dashed border-brand bg-surface/70 text-small font-bold text-brand-ink"
          aria-hidden
        >
          Rilascia qui
        </div>
      ) : null}
      {righe.length ? (
        <ol className="grid grid-cols-1 content-start gap-2 overflow-y-auto md:max-h-[calc(100dvh-22rem)]">
          {ordina(righe, codice).map(carta)}
        </ol>
      ) : rilascia ? null : (
        <p className="rounded-lg border border-dashed border-line-chip px-group py-card text-center text-small text-ink-3">Nessuno qui</p>
      )}
    </section>
  );
}

function GestoDialog({ gesto, onChiudi, onFuori }) {
  const nav = useNavigate();
  const g = gesto?.gesto || {};
  return (
    <Dialog open={Boolean(gesto)} onOpenChange={(o) => !o && onChiudi()}>
      <DialogContent data-testid="kanban-gesto-modal">
        <DialogHeader>
          <DialogTitle>
            {gesto?.nome} in «{gesto ? etichetta(gesto.a) : ""}»
          </DialogTitle>
          <DialogDescription>
            {gesto?.messaggio}
            {g.nota ? ` ${g.nota}` : ""}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-inside">
          <Button type="button" variant="outline" onClick={onChiudi}>
            {g.vai ? "Non ora" : "Ho capito"}
          </Button>
          {g.fuori ? (
            <Button type="button" variant="outline" onClick={onFuori} data-testid="kanban-gesto-fuori">
              Fatto fuori, allegalo
            </Button>
          ) : null}
          {g.vai ? (
            <Button type="button" onClick={() => nav(g.vai)} data-testid="kanban-gesto-vai">
              {g.azione}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function KanbanClienti({ righe, onCambiato = () => {} }) {
  const [spostati, setSpostati] = useState({}); // id → colonna, finché il server non risponde
  const [presa, setPresa] = useState(null); // la carta che segue il puntatore: { r, w }
  const [sopra, setSopra] = useState(null); // la colonna su cui si lascerebbe
  const [tastiera, setTastiera] = useState(null); // { r, idx }: la carta presa coi tasti
  const [annuncio, setAnnuncio] = useState("");
  const [chiediMotivo, setChiediMotivo] = useState(null);
  const [gesto, setGesto] = useState(null);
  const [fuori, setFuori] = useState(null); // il «Fatto fuori» aperto dal gesto: { r, passo, a }
  const telefono = !useMedia("(min-width: 768px)");
  const tabellone = useRef(null);
  const fantasma = useRef(null);
  const trascina = useRef(null);
  const lasciataAlle = useRef(0);
  const daRifocalizzare = useRef(null);

  useEffect(() => setSpostati({}), [righe]);

  // Lasciata coi tasti, la carta rinasce nell'altra colonna (due volte: subito e dopo la
  // ricarica) e il focus andrebbe perso. Torna su di lei solo se è un elemento nuovo, il
  // focus è caduto sul body e sono passati pochi secondi: chi poi clicca altrove se lo tiene.
  useEffect(() => {
    const r = daRifocalizzare.current;
    if (!r || Date.now() > r.fino || document.activeElement !== document.body) return;
    const el = tabellone.current?.querySelector(`[data-testid="kanban-carta-${r.id}"]`);
    if (el && el !== r.el) {
      r.el = el;
      el.focus();
    }
  });

  const colonnaDi = (r) => spostati[r.id] || r.stato;
  const perStato = {};
  for (const r of righe) (perStato[colonnaDi(r)] ||= []).push(r);

  /* `riprendi`: col Perso, la prossima azione «Risentirlo» fra tre mesi (decisione del 30-10). Si scrive dopo lo
     spostamento; «Annulla» rimette anche la prossima azione di prima, che `annulla_spostamento` non conosce. */
  async function esegui(r, a, motivo, riprendi = false) {
    setSpostati((m) => ({ ...m, [r.id]: a }));
    try {
      const { data } = await api.post(`/crm/clienti/${r.id}/sposta`, { da: r.stato, a, motivo });
      setAnnuncio(`${r.nome} spostato in ${etichetta(a)}.`);
      const ripresa = riprendi ? await riprendiFra3Mesi(r) : null;
      if (data?.spostamento) {
        toast.success(
          ripresa ? `${r.nome} in «${etichetta(a)}», da riprendere ${giornoLungo(ripresa)}` : `${r.nome} in «${etichetta(a)}»`,
          {
            duration: ANNULLA_PER_MS,
            action: { label: "Annulla", onClick: () => annulla(data.spostamento, r, Boolean(ripresa)) },
          },
        );
      }
      onCambiato();
      return true;
    } catch (e) {
      setSpostati((m) => {
        const { [r.id]: _, ...resto } = m;
        return resto;
      });
      const d = dettaglio(e);
      setAnnuncio(`${r.nome} resta in ${etichetta(r.stato)}.`);
      if (d?.gesto) setGesto({ ...d, nome: r.nome, a, r });
      else toast.error(erroreDi(e));
      if (d?.codice === "cambiato") onCambiato();
      return false;
    }
  }

  /* Registrato il fatto fuori la carta va dove dicono i fatti. Se si stava trascinando verso «In trattativa» lo
     spostamento si rifà: ora il cliente è in «Preventivo inviato» e il passaggio è ammesso. */
  async function fattoFuori() {
    const { r, a } = fuori;
    setFuori(null);
    const riuscito = a === "in_trattativa" && (await esegui({ ...r, stato: "preventivo_inviato" }, "in_trattativa"));
    if (!riuscito) onCambiato();
  }

  /** Scrive la ripresa e restituisce il giorno, o null se non è andata (lo dice un avviso). */
  async function riprendiFra3Mesi(r) {
    const giorno3 = fraTreMesi();
    try {
      await api.patch(`/crm/clienti/${r.id}`, { prossima_azione_giorno: giorno3, prossima_azione: "Risentirlo" });
      return giorno3;
    } catch (e) {
      toast.error(`Segnato perso, la ripresa no: ${erroreDi(e)}`);
      return null;
    }
  }

  async function annulla(spostamento, r, conRipresa = false) {
    try {
      await api.post(`/crm/spostamenti/${spostamento}/annulla`);
      if (conRipresa)
        await api.patch(
          `/crm/clienti/${r.id}`,
          r.prossima_azione_at
            ? { prossima_azione_giorno: giornoDi(r.prossima_azione_at), prossima_azione: r.prossima_azione || "Richiamare" }
            : { togli_prossima_azione: true },
        );
      toast.success(`${r.nome} è tornato in «${etichetta(r.stato)}»`);
      setAnnuncio(`Annullato: ${r.nome} di nuovo in ${etichetta(r.stato)}.`);
    } catch (e) {
      toast.error(erroreDi(e));
    }
    onCambiato();
  }

  function lascia(r, a) {
    if (!a || a === r.stato) {
      setAnnuncio(`${r.nome} resta in ${etichetta(r.stato)}.`);
      return;
    }
    if (a === "perso") setChiediMotivo(r);
    else esegui(r, a);
  }

  /* ── Mouse e dito: Pointer Events, un codice solo ──────────────────────────
     Col mouse si parte dopo 5 px di movimento; col dito tenendo premuto, così lo
     scorrimento del tabellone resta allo scorrimento. Il fantasma è fisso sullo
     schermo e segue il puntatore senza ridisegnare React; la colonna sotto si
     trova con elementFromPoint (il fantasma non prende i clic). */
  function colonnaSotto(x, y) {
    return document.elementFromPoint(x, y)?.closest("[data-colonna]")?.dataset.colonna || null;
  }

  function posiziona(t) {
    if (fantasma.current) {
      fantasma.current.style.transform = `translate(${t.x - t.offX}px, ${t.y - t.offY}px) rotate(1.5deg)`;
    }
    const c = colonnaSotto(t.x, t.y);
    if (c !== t.sopra) {
      t.sopra = c;
      setSopra(c);
    }
  }

  function scorri(t) {
    // Vicino al bordo del tabellone si scorre verso la colonna che non si vede.
    const box = tabellone.current?.getBoundingClientRect();
    if (box) {
      const v = t.x < box.left + 48 ? -14 : t.x > box.right - 48 ? 14 : 0;
      if (v) {
        tabellone.current.scrollLeft += v;
        posiziona(t);
      }
    }
    t.raf = requestAnimationFrame(() => scorri(t));
  }

  function attiva(t) {
    if (trascina.current !== t) return; // il timer di un tocco già chiuso
    t.attiva = true;
    setPresa({ r: t.r, w: t.w, x: t.x - t.offX, y: t.y - t.offY });
    setSopra(t.r.stato);
    t.sopra = t.r.stato;
    setAnnuncio(`Preso ${t.r.nome}.`);
    navigator.vibrate?.(10);
    requestAnimationFrame(() => posiziona(t));
    t.raf = requestAnimationFrame(() => scorri(t));
  }

  function smetti() {
    const t = trascina.current;
    if (!t) return;
    clearTimeout(t.timer);
    cancelAnimationFrame(t.raf);
    // Gli stessi gestori aggiunti in `inizia`: fra un render e l'altro le funzioni cambiano.
    window.removeEventListener("pointermove", t.h.muovi);
    window.removeEventListener("pointerup", t.h.fine);
    window.removeEventListener("pointercancel", t.h.smetti);
    window.removeEventListener("touchmove", t.h.blocca);
    trascina.current = null;
    setPresa(null);
    setSopra(null);
  }

  function blocca(e) {
    // Mentre la carta è presa il dito trascina, non scorre la pagina.
    if (trascina.current?.attiva && e.cancelable) e.preventDefault();
  }

  function muovi(e) {
    const t = trascina.current;
    if (!t || e.pointerId !== t.id) return;
    t.x = e.clientX;
    t.y = e.clientY;
    if (!t.attiva) {
      const d = Math.hypot(t.x - t.x0, t.y - t.y0);
      if (t.tipo === "mouse" && d > 5) attiva(t);
      else if (t.tipo !== "mouse" && d > 10) smetti(); // il dito scorre: non è un trascinamento
      return;
    }
    posiziona(t);
  }

  function fine(e) {
    const t = trascina.current;
    if (!t || e.pointerId !== t.id) return;
    const attiva = t.attiva;
    const a = t.sopra;
    smetti();
    if (attiva) {
      lasciataAlle.current = Date.now(); // il clic che segue il rilascio non apre la scheda
      lascia(t.r, a);
    }
  }

  function inizia(e, r) {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    // Un dito alla volta: il secondo (un pinch, un tocco in più) non apre un altro trascinamento.
    if (tastiera || trascina.current || !e.isPrimary) return;
    const box = e.currentTarget.getBoundingClientRect();
    const t = {
      r,
      id: e.pointerId,
      tipo: e.pointerType,
      x0: e.clientX,
      y0: e.clientY,
      x: e.clientX,
      y: e.clientY,
      offX: e.clientX - box.left,
      offY: e.clientY - box.top,
      w: box.width,
      attiva: false,
      h: { muovi, fine, smetti, blocca },
    };
    trascina.current = t;
    daRifocalizzare.current = null;
    if (t.tipo !== "mouse") t.timer = setTimeout(() => attiva(t), TIENI_PREMUTO_MS);
    window.addEventListener("pointermove", muovi);
    window.addEventListener("pointerup", fine);
    window.addEventListener("pointercancel", smetti);
    window.addEventListener("touchmove", blocca, { passive: false });
  }

  useEffect(() => () => smetti(), []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Tastiera: Spazio prende, frecce scelgono la colonna, Spazio o Invio lasciano,
     Esc rimette. Ogni passo lo annuncia la regione viva per i lettori di schermo. */
  function tasto(e, r) {
    const presaQui = tastiera?.r.id === r.id;
    if (!presaQui) {
      if (e.key === " ") {
        e.preventDefault();
        const idx = COLONNE.indexOf(colonnaDi(r));
        setTastiera({ r, idx });
        setAnnuncio(
          `Preso ${r.nome}, in ${etichetta(colonnaDi(r))}. Frecce destra e sinistra per scegliere la colonna, Spazio per lasciarlo, Esc per annullare.`,
        );
      }
      return;
    }
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const idx = (tastiera.idx + (e.key === "ArrowRight" ? 1 : -1) + COLONNE.length) % COLONNE.length;
      setTastiera({ r, idx });
      setAnnuncio(`Sopra ${etichetta(COLONNE[idx])}.`);
      tabellone.current?.querySelector(`[data-colonna="${COLONNE[idx]}"]`)?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
    } else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      setTastiera(null);
      daRifocalizzare.current = { id: r.id, el: e.currentTarget, fino: Date.now() + 4000 };
      lascia(r, COLONNE[tastiera.idx]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setTastiera(null);
      setAnnuncio(`${r.nome} resta in ${etichetta(r.stato)}.`);
    }
  }

  const evidenziata = tastiera ? COLONNE[tastiera.idx] : sopra;

  function carta(r) {
    const tenuta = presa?.r.id === r.id || tastiera?.r.id === r.id;
    const conEsito = esitoDaSegnare(r);
    const at = r.appuntamento_senza_esito_at;
    return (
      <li
        key={r.id}
        className={cn(
          CARTA,
          "relative transition-[border-color,opacity] duration-150 ease-enter hover:border-line-input",
          presa?.r.id === r.id && "opacity-40",
          tastiera?.r.id === r.id && "border-brand ring-2 ring-brand",
        )}
      >
        <Link
          to={`/clienti/${r.id}`}
          data-testid={`kanban-carta-${r.id}`}
          aria-describedby="kanban-istruzioni"
          onPointerDown={(e) => inizia(e, r)}
          onKeyDown={(e) => tasto(e, r)}
          onBlur={() => tastiera?.r.id === r.id && setTastiera(null)}
          onClick={(e) => Date.now() - lasciataAlle.current < 500 && e.preventDefault()}
          onContextMenu={(e) => trascina.current && e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          className={cn(
            "grid grid-cols-1 gap-2 rounded-sm select-none [-webkit-touch-callout:none] md:cursor-grab",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand",
            telefono && "pr-11",
            tenuta && "md:cursor-grabbing",
          )}
        >
          <Contenuto r={r} conEsito={conEsito} />
        </Link>
        {conEsito ? (
          <div className="grid gap-1.5 rounded-sm bg-warning-bg p-2" data-testid={`kanban-esito-${r.id}`}>
            <p className="text-caption text-warning-ink">
              Visita {giornoDi(at) === oggi() ? "delle" : `di ${giorno(at)}, alle`} {ora(at)} · com'è andata?
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                type="button"
                size="sm"
                className="h-11 md:h-8"
                onClick={() => esegui(r, "appuntamento_fatto")}
                data-testid={`kanban-presentata-${r.id}`}
              >
                Presentata
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-11 text-danger-ink md:h-8"
                onClick={() => esegui(r, "no_show")}
                data-testid={`kanban-noshow-${r.id}`}
              >
                No show
              </Button>
            </div>
          </div>
        ) : null}
        {telefono ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Sposta ${r.nome} in un'altra colonna`}
                className="absolute right-1 top-1 inline-flex size-11 items-center justify-center rounded-sm text-ink-2 hover:bg-surface-2"
                data-testid={`kanban-sposta-${r.id}`}
              >
                <ArrowLeftRight className="size-5" aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Sposta in</DropdownMenuLabel>
              {COLONNE.filter((c) => c !== colonnaDi(r)).map((c) => (
                <DropdownMenuItem key={c} className="min-h-11" onSelect={() => lascia(r, c)}>
                  {etichetta(c)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </li>
    );
  }

  /* La colonna sotto la carta presa mostra «Rilascia qui» (non quella da cui parte). */
  const daDove = presa ? colonnaDi(presa.r) : tastiera ? colonnaDi(tastiera.r) : null;
  const vai = (c) =>
    tabellone.current?.querySelector(`[data-colonna="${c}"]`)?.scrollIntoView?.({ behavior: "smooth", block: "nearest", inline: "start" });

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-group">
      <p className="sr-only" id="kanban-istruzioni">
        Trascina le schede per spostarle; dal telefono tieni premuto, o usa il pulsante Sposta. Da tastiera: Spazio prende, le frecce
        scelgono la colonna, Spazio lascia.
      </p>
      <p className="sr-only" aria-live="assertive" data-testid="kanban-annuncio">
        {annuncio}
      </p>
      <Riepilogo perStato={perStato} onVai={vai} />
      <div
        ref={tabellone}
        className="-mx-4 overflow-x-auto px-4 pb-group md:mx-0 md:px-0"
        data-testid="clienti-kanban"
        tabIndex={0}
        aria-label="Clienti per stato commerciale"
      >
        <div className="flex w-max snap-x snap-mandatory items-start gap-3.5 md:snap-none">
          {COLONNE.map((c) => (
            <Colonna
              key={c}
              codice={c}
              righe={perStato[c] || []}
              evidenziata={evidenziata === c}
              rilascia={evidenziata === c && daDove !== c}
              carta={carta}
            />
          ))}
        </div>
      </div>
      {presa ? (
        <div
          ref={fantasma}
          aria-hidden
          data-testid="kanban-fantasma"
          className={cn(CARTA, "pointer-events-none fixed left-0 top-0 z-50 border-[1.5px] border-brand shadow-overlay")}
          style={{ width: presa.w, transform: `translate(${presa.x}px, ${presa.y}px) rotate(1.5deg)` }}
        >
          <Contenuto r={presa.r} />
        </div>
      ) : null}
      <PersoDialog
        nome={chiediMotivo?.nome}
        open={Boolean(chiediMotivo)}
        prefisso="kanban-perso"
        verbo="Sposta in Perso"
        onOpenChange={(o) => {
          if (o) return;
          setAnnuncio(`${chiediMotivo?.nome} resta in ${etichetta(chiediMotivo?.stato)}.`);
          setChiediMotivo(null);
        }}
        onConferma={async (motivo, riprendi) => {
          const r = chiediMotivo;
          setChiediMotivo(null);
          await esegui(r, "perso", motivo, riprendi);
        }}
      />
      <GestoDialog
        gesto={gesto}
        onChiudi={() => setGesto(null)}
        onFuori={() => {
          setFuori({ r: gesto.r, passo: gesto.gesto.fuori, a: gesto.a });
          setGesto(null);
        }}
      />
      {fuori ? (
        <FattoFuori cliente={{ id: fuori.r.id, nome: fuori.r.nome }} passo={fuori.passo} onChiudi={() => setFuori(null)} onFatto={fattoFuori} />
      ) : null}
    </div>
  );
}
